import React from "react";
import { brand } from "../theme/theme";
import { Box, Typography, Avatar, Skeleton } from "@mui/material";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { Users } from "lucide-react";
import { useTheme } from "../theme/useTheme";
import { useAuth } from "./contexts/AuthContext";

import { Badge } from "./Badge";
import { UnifiedModal } from "./UnifiedModal";

/**
 * MembersModal
 * Modal para mostrar los participantes de un thread de conversación
 *
 * Props:
 * - open (boolean): Si el modal está abierto
 * - onClose (function): Callback para cerrar el modal
 * - participants (Array): Lista de participantes con id, full_name, email, role
 * - clientName (string): Nombre del cliente para el título
 * - agencyName (string): Nombre de la agencia para mostrar debajo del título
 * - title (string): Título personalizado del modal (opcional)
 * - showBadges (boolean): Mostrar badges de rol (opcional)
 * - showAgencyName (boolean): Mostrar el nombre de la agencia (opcional)
 * - isLoading (boolean): Mostrar skeletons mientras se cargan los miembros
 */
export function MembersModal({
  open,
  onClose,
  participants = [],
  clientName = "",
  agencyName = "",
  title = null,
  showBadges = true,
  showAgencyName = true,
  isLoading = false,
}) {
  const theme = useMuiTheme();
  const { neutrals, roleBadges } = useTheme();
  const { user: currentUser } = useAuth();

  // Función para obtener la etiqueta del rol
  const getRoleLabel = (role) => {
    const roleLabels = {
      superadmin: "Super Admin",
      agency_admin: "Administrador",
      consultant: "Consultor",
      client: "Cliente",
    };
    return roleLabels[role] || role;
  };

  const sortedParticipants = [...participants].sort((a, b) => {
    const rolePriority = {
      superadmin: 1,
      agency_admin: 2,
      consultant: 3,
      client: 4,
    };
    const priorityA = rolePriority[a.role] || 99;
    const priorityB = rolePriority[b.role] || 99;
    if (priorityA !== priorityB) return priorityA - priorityB;
    return (a.full_name || "").localeCompare(b.full_name || "");
  });

  // Función para obtener estilos del badge según rol
  const getBadgeStyles = (role) => {
    // Normalizar rol por seguridad
    const badgeStyle = roleBadges[role] || roleBadges.default;

    return {
      backgroundColor: badgeStyle.backgroundColor,
      color: badgeStyle.color,
      borderColor: badgeStyle.borderColor,
      // Estilos específicos para badges tipo outline/soft
      borderWidth: "1px",
      borderStyle: "solid",
    };
  };

  // Use custom title if provided, otherwise use default
  const modalTitle = title || `Miembros de la conversación: ${clientName}`;
  const showEmptyState = !isLoading && participants.length === 0;

  return (
    <UnifiedModal
      open={open}
      onClose={onClose}
      title={modalTitle}
      maxWidth="sm"
    >
      {agencyName && showAgencyName && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Agencia: {agencyName}
          </Typography>
        </Box>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {isLoading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <Box
              key={`member-skeleton-${index}`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                p: 1.5,
                borderRadius: "10px",
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
              }}
            >
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="55%" height={18} />
                <Skeleton variant="text" width="35%" height={16} />
              </Box>
              {showBadges ? (
                <Skeleton variant="rounded" width={72} height={20} />
              ) : null}
            </Box>
          ))
        ) : showEmptyState ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 4,
              gap: 2,
            }}
          >
            <Users size={48} color="#9ca3af" />
            <Typography variant="body2" color="text.secondary">
              No hay participantes disponibles
            </Typography>
          </Box>
        ) : (
          sortedParticipants.map((participant) => (
            <Box
              key={participant.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                p: 1.5,
                borderRadius: "10px",
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              {/* Avatar circular del color primario de la app */}
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: brand.avatar,
                  color: "#fff",
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  border: `1px solid ${neutrals.gray[300]}`,
                }}
                src={participant.profile_image || undefined}
                alt={participant.full_name || "Usuario"}
              >
                {(participant.full_name || "U")
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()
                  .slice(0, 2)}
              </Avatar>

              {/* Información del participante */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: showBadges ? "space-between" : "flex-start",
                    width: "100%",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: "text.primary",
                      lineHeight: 1.2,
                    }}
                  >
                    {participant.full_name}
                  </Typography>
                  {showBadges && (
                    <Badge
                      sx={{
                        ...getBadgeStyles(participant.role),
                      }}
                    >
                      {getRoleLabel(participant.role)}
                    </Badge>
                  )}
                </Box>

                {currentUser?.role !== "client" && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.75rem",
                      lineHeight: 1.2,
                    }}
                  >
                    {participant.email}
                  </Typography>
                )}
              </Box>
            </Box>
          ))
        )}
      </Box>
    </UnifiedModal>
  );
}
